// Core React imports
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

// Third-party libraries
import {
  AlertCircle,
  AlertTriangle,
  ArrowRight,
  CheckCircle,
  Info,
  Mic,
  RotateCcw,
  Square,
} from "react-feather";

// App imports
import { useDeepgramIntegration } from "../../services/deepgram/DeepgramSTT.js";
import useMicLevel from "../../services/audio/useMicLevel.js";
import { matchPhrase, MIN_THRESHOLD, PASS_THRESHOLD } from "../../utils/text/phrase-match.js";
import {
  ANALYZE_GRACE_MS,
  MAX_ATTEMPTS_BEFORE_SKIP,
  MAX_RECORD_MS,
  SILENCE_LEVEL_THRESHOLD,
  SILENCE_STOP_MS,
  SPEAKING_LEVEL_THRESHOLD,
  getRandomPhrase,
} from "../../libs/constants/mic-check.js";

// Assets and styles
import "../../styles/MicCheck.css";

// Copy and styling for each outcome band. Kept as one lookup table so the
// render below stays a straight prop-lookup rather than a chain of
// conditionals for every string/color.
const BAND_CONTENT = {
  pass: {
    icon: CheckCircle,
    wrapClass: "bg-app-successLight text-app-success border-app-successLight",
    badgeClass: "bg-app-success text-white",
    title: "Your microphone sounds great",
    body: "We heard you clearly. You're all set to begin.",
    showTips: false,
  },
  warn: {
    icon: AlertTriangle,
    wrapClass: "bg-app-secondaryLight text-app-secondary border-app-secondaryLight",
    badgeClass: "bg-app-secondary text-white",
    title: "We could hear you, but not perfectly",
    body: "Some words came through unclearly. If you can, move somewhere quieter or a little closer to your microphone before you begin.",
    showTips: true,
  },
  fail: {
    icon: AlertCircle,
    wrapClass: "bg-app-dangerLight text-app-danger border-app-dangerLight",
    badgeClass: "bg-app-danger text-white",
    title: "We couldn't make out the sentence",
    body: "Let's try once more — read at a normal pace, a little louder than before.",
    showTips: true,
  },
  silent: {
    icon: AlertCircle,
    wrapClass: "bg-app-dangerLight text-app-danger border-app-dangerLight",
    badgeClass: "bg-app-danger text-white",
    title: "We didn't detect any sound",
    body: "Your microphone didn't pick up any audio. Check that it isn't muted in System Settings and that the right input device is selected.",
    showTips: true,
  },
  error: {
    icon: Info,
    wrapClass: "bg-app-infoLight text-app-info border-app-infoLight",
    badgeClass: "bg-app-info text-white",
    title: "We couldn't run the microphone check",
    body: "This is on our side, not yours — you can go ahead and start your interview.",
    showTips: false,
  },
};

const TIPS = [
  "Check if Microphone Input is enabled & working fine on your System",
  "Move a little closer to your microphone",
  "Find a quieter spot if one is nearby",
  "If still facing issues, try reloading this tab and try again",
  "If nothing works, continue with the interview anayway and see if it works for you"
];

/**
 * Step 3 of device setup: has the candidate read a short sentence aloud,
 * transcribes it live with Deepgram, and scores the transcript against the
 * expected phrase to catch a muted mic, wrong input device, or noisy room
 * before it silently degrades the real interview transcript.
 *
 * Deliberately reuses the same MediaStream the interview itself will use
 * (`permissions.micStream.stream`, passed in as `micStream`) rather than
 * requesting a second one - see useDeepgramSTT's micStream option, which is
 * built exactly for this kind of reuse.
 */
const MicCheck = ({ micStream, onPassed, onSkipped }) => {
  const [phrase, setPhrase] = useState(() => getRandomPhrase());
  const [checkState, setCheckState] = useState("idle"); // idle | connecting | listening | analyzing | result
  const [result, setResult] = useState(null); // { band, score, matchedWords }
  const [attempts, setAttempts] = useState(0);
  const [liveTranscript, setLiveTranscript] = useState("");

  // Mirrors `phrase`, but read (not closed-over) at scoring time. A retry
  // calls setPhrase() and then, synchronously in the same tick, calls
  // startRecording() - before React has re-rendered. If finishAnalysis
  // closed over `phrase` directly, that startRecording call would still be
  // built from the *previous* render's finishAnalysis/stopRecording chain
  // (useCallback identities don't update mid-tick), so the setTimeout it
  // schedules would score the recording against the phrase from before the
  // retry, not the one now on screen - every retry one phrase behind. A ref
  // sidesteps this: it's updated eagerly in handleRetry, and finishAnalysis
  // reads it live, so it's correct no matter which render created the
  // closure that eventually calls it.
  const phraseRef = useRef(phrase);

  const finalChunksRef = useRef([]);
  const interimRef = useRef("");
  const analyzeTimeoutRef = useRef(null);
  const recordCapRef = useRef(null);
  const silenceTimerRef = useRef(null);
  const hasSpokenRef = useRef(false);

  const { level, getPeakLevel, start: startLevelMeter, stop: stopLevelMeter } = useMicLevel();

  const clearTimers = useCallback(() => {
    if (analyzeTimeoutRef.current) clearTimeout(analyzeTimeoutRef.current);
    if (recordCapRef.current) clearTimeout(recordCapRef.current);
    if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
    analyzeTimeoutRef.current = null;
    recordCapRef.current = null;
    silenceTimerRef.current = null;
  }, []);

  const handleTranscript = useCallback((transcript, isFinal) => {
    if (!transcript) return;
    // useDeepgramSTT replaces finalTranscript on every is_final segment
    // rather than appending, so a multi-segment sentence would otherwise
    // only ever be scored on its last fragment - accumulate finals here.
    if (isFinal) {
      finalChunksRef.current.push(transcript);
      interimRef.current = "";
    } else {
      interimRef.current = transcript;
    }
    setLiveTranscript([...finalChunksRef.current, interimRef.current].join(" ").trim());
  }, []);

  const finishAnalysis = useCallback(() => {
    const transcript = [...finalChunksRef.current, interimRef.current].join(" ").trim();
    const peak = getPeakLevel();

    if (!transcript && peak < SILENCE_LEVEL_THRESHOLD) {
      setResult({ band: "silent", score: 0, matchedWords: [] });
      setCheckState("result");
      return;
    }

    const { score, matchedWords } = matchPhrase(phraseRef.current, transcript);
    const band = score >= PASS_THRESHOLD ? "pass" : score >= MIN_THRESHOLD ? "warn" : "fail";
    setResult({ band, score, matchedWords });
    setCheckState("result");
  }, [getPeakLevel]);

  const handleDeepgramError = useCallback(() => {
    clearTimers();
    stopLevelMeter();
    setResult({ band: "error", score: 0, matchedWords: [] });
    setCheckState("result");
  }, [clearTimers, stopLevelMeter]);

  const { startListening, stopListening, disconnect, resetTranscripts } = useDeepgramIntegration({
    micStream,
    // Punctuation/number formatting would only complicate comparing against
    // a fixed expected phrase - the interview's own Deepgram usage is
    // unaffected since this is a separate hook instance.
    smartFormat: false,
    endpointing: 250,
    autoStart: false,
    onRealtimeTranscript: handleTranscript,
    onError: handleDeepgramError,
  });

  const stopRecording = useCallback(() => {
    setCheckState((current) => {
      if (current !== "listening") return current;
      clearTimers();
      stopListening();
      stopLevelMeter();
      analyzeTimeoutRef.current = setTimeout(finishAnalysis, ANALYZE_GRACE_MS);
      return "analyzing";
    });
  }, [clearTimers, stopListening, stopLevelMeter, finishAnalysis]);

  const startRecording = useCallback(async () => {
    // Every attempt - including retries - starts by force-closing whatever
    // Deepgram connection remains from the previous one and clearing any
    // leftover timers, so it always begins from a genuinely clean slate
    // rather than reusing a connection Deepgram already considers closing
    // (see disconnect()'s doc comment in DeepgramSTT.js for why that broke
    // transcription on retries). Safe on the very first attempt too - both
    // are no-ops when there's nothing to clean up yet.
    disconnect();
    clearTimers();
    finalChunksRef.current = [];
    interimRef.current = "";
    hasSpokenRef.current = false;
    setLiveTranscript("");
    setResult(null);
    resetTranscripts();
    setAttempts((a) => a + 1);
    setCheckState("connecting");

    try {
      await startListening();
      setCheckState("listening");
      startLevelMeter(micStream);
      recordCapRef.current = setTimeout(stopRecording, MAX_RECORD_MS);
    } catch (err) {
      setResult({ band: "error", score: 0, matchedWords: [] });
      setCheckState("result");
    }
  }, [disconnect, clearTimers, startListening, startLevelMeter, micStream, resetTranscripts, stopRecording]);

  // Auto-stop once the candidate has clearly started, then clearly stopped,
  // speaking - so they don't have to remember to tap "done" mid-flow.
  useEffect(() => {
    if (checkState !== "listening") return undefined;

    if (level >= SPEAKING_LEVEL_THRESHOLD) {
      hasSpokenRef.current = true;
      if (silenceTimerRef.current) {
        clearTimeout(silenceTimerRef.current);
        silenceTimerRef.current = null;
      }
      return undefined;
    }

    if (hasSpokenRef.current && !silenceTimerRef.current) {
      silenceTimerRef.current = setTimeout(stopRecording, SILENCE_STOP_MS);
    }
    return undefined;
  }, [level, checkState, stopRecording]);

  // Belt-and-braces cleanup if the candidate navigates away mid-check.
  // useDeepgramSTT's own unmount effect tears down its socket/recorder and
  // deliberately does not stop micStream's tracks since it's external.
  useEffect(() => {
    return () => {
      clearTimers();
      stopLevelMeter();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Automatically rotates to a different phrase on every retry, rather than
  // asking the candidate to request one via a separate control. Updates
  // phraseRef synchronously, before startRecording() runs in this same
  // tick - see the comment on phraseRef's declaration for why that matters.
  const handleRetry = useCallback(() => {
    const nextPhrase = getRandomPhrase(phraseRef.current);
    phraseRef.current = nextPhrase;
    setPhrase(nextPhrase);
    startRecording();
  }, [startRecording]);

  const handlePrimaryAction = useCallback(() => {
    if (checkState === "idle") startRecording();
    else if (checkState === "listening") stopRecording();
    else if (checkState === "result" && result && result.band !== "pass") handleRetry();
  }, [checkState, startRecording, stopRecording, result, handleRetry]);

  // Live word-by-word highlighting while the candidate is still speaking,
  // frozen to the final scored result once analysis completes.
  const liveMatch = useMemo(() => matchPhrase(phrase, liveTranscript), [phrase, liveTranscript]);
  const matchedWords = result ? result.matchedWords : liveMatch.matchedWords;
  const phraseWords = useMemo(() => phrase.split(" "), [phrase]);

  const band = result ? BAND_CONTENT[result.band] : null;
  const BandIcon = band ? band.icon : null;

  const canOfferSkip =
    result && (result.band === "fail" || result.band === "silent") && attempts >= MAX_ATTEMPTS_BEFORE_SKIP;
  // Once a non-pass result lands, retrying happens by tapping the (now
  // pulsing) mic button itself rather than a separate "Try again" button.
  const isRetryPrompt = checkState === "result" && band && result.band !== "pass";

  const primaryBtnClass =
    "inline-flex items-center gap-x-1.5 px-5 py-2 rounded-md bg-app-primary text-white text-sm font-medium cursor-pointer shadow-sm hover:shadow-md hover:bg-app-primary/90 transition";
  const outlineBtnClass =
    "inline-flex items-center gap-x-1.5 px-5 py-2 rounded-md border border-gray-300 text-gray-600 text-sm font-medium cursor-pointer hover:bg-gray-50 hover:border-gray-400 transition";

  return (
    <div className="mt-3 w-full" data-testid="mic-check">
      {/* The subtitle is onboarding-only - once the candidate has started,
          collapsing to just the title reclaims vertical space for the parts
          that matter most right then (result, tips, actions). */}
      <div className="flex flex-col items-center text-center">
        <h5 className="text-gray-900 text-base font-semibold">Microphone Check</h5>
        {checkState === "idle" && (
          <p className="max-w-sm mt-1 text-xs text-gray-500">
            Read the sentence below out loud, in your normal speaking voice.
          </p>
        )}
      </div>

      <div className="max-w-xl mx-auto">
      {/* Phrase card - words light up green as Deepgram recognizes them */}
      <div className="bg-gradient-to-b from-[#f5f7ff] to-[#fafbff] border border-[#dce3fd] rounded-xl shadow-sm p-3.5 mt-3">
        <p className="text-[10px] font-semibold tracking-wide text-gray-400 uppercase mb-1.5">
          Read this out loud
        </p>
        <p
          className="text-base sm:text-lg font-medium text-[#101828] leading-relaxed"
          data-testid="mic-check-phrase"
        >
          {phraseWords.map((word, i) => (
            <span
              key={`${i}-${word}`}
              className={matchedWords[i] ? "text-app-success transition-colors" : "transition-colors"}
            >
              {word}
              {i < phraseWords.length - 1 ? " " : ""}
            </span>
          ))}
        </p>
      </div>

      {/* Mic control - deliberately borderless/backgroundless (unlike the
          phrase card and result banner) so the page doesn't read as a stack
          of nested boxes; this is just the interactive centerpiece. */}
      <div className="flex flex-col items-center justify-center mt-5 mb-1">
        {isRetryPrompt && (
          <p className="text-sm font-semibold text-app-primary mb-2">Try Again</p>
        )}

        <div className="relative flex items-center justify-center h-16 w-16">
          {checkState === "idle" && (
            <span className="absolute inline-block h-full w-full rounded-full bg-app-primary/20 mic-check-breathe" />
          )}
          {checkState === "listening" && (
            <>
              <span className="absolute inline-flex h-full w-full rounded-full bg-app-danger/20 animate-ping" />
              <span
                className="absolute inline-flex h-[80%] w-[80%] rounded-full bg-app-danger/20 animate-ping"
                style={{ animationDelay: "0.4s" }}
              />
            </>
          )}
          {isRetryPrompt && (
            <>
              <span className="absolute inline-flex h-full w-full rounded-full bg-app-primary/20 animate-ping" />
              <span
                className="absolute inline-flex h-[80%] w-[80%] rounded-full bg-app-primary/20 animate-ping"
                style={{ animationDelay: "0.4s" }}
              />
            </>
          )}
        <button
          type="button"
          onClick={handlePrimaryAction}
          disabled={checkState === "connecting" || checkState === "analyzing" || !micStream}
          data-testid="mic-check-toggle"
          data-state={checkState}
          className={`relative w-16 h-16 rounded-full flex items-center justify-center transition shadow-lg ${
            checkState === "listening"
              ? "bg-app-danger text-white"
              : "bg-app-primary text-white hover:bg-app-primaryHover2 hover:scale-105"
          } ${
            checkState === "connecting" || checkState === "analyzing" || !micStream
              ? "opacity-60 cursor-not-allowed hover:scale-100"
              : "cursor-pointer"
          }`}
          aria-label={
            checkState === "listening" ? "Stop recording" : isRetryPrompt ? "Try again" : "Start microphone check"
          }
        >
          {checkState === "listening" ? (
            <Square size={20} />
          ) : isRetryPrompt ? (
            <RotateCcw size={20} />
          ) : (
            <Mic size={22} />
          )}
        </button>
        </div>

        {/* One line, always - a live caption while listening, otherwise a
            short status. Replaces what used to be a separate status row plus
            a separate "what we heard" box. */}
        <p
          className="mt-3 text-[13px] text-gray-500 text-center min-h-[20px] px-6 max-w-md"
          data-testid="mic-check-transcript"
        >
          {checkState === "idle" && "Tap the mic to start"}
          {checkState === "connecting" && "Connecting..."}
          {checkState === "listening" && (
            <span className="inline-flex items-center gap-x-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-app-danger animate-pulse shrink-0" />
              <span className="italic text-gray-700">
                {liveTranscript || "Listening..."}
                <span className="mic-check-cursor">&nbsp;|</span>
              </span>
            </span>
          )}
          {checkState === "analyzing" && "Analyzing your voice..."}
          {checkState === "result" && (
            <span className="italic">{liveTranscript ? `"${liveTranscript}"` : "We didn't hear anything"}</span>
          )}
        </p>
      </div>

      {/* Result banner */}
      {band && (
        <div
          className={`mt-3 flex gap-3 text-sm px-4 py-2.5 rounded-xl border shadow-sm ${band.wrapClass}`}
          data-testid="mic-check-result"
          data-band={result.band}
        >
          <div className={`shrink-0 w-9 h-9 rounded-full flex items-center justify-center ${band.badgeClass}`}>
            <BandIcon size={17} />
          </div>
          <div className="flex-1 pt-0.5">
            <p className="text-[13px] font-semibold">{band.title}</p>
            <p className="text-[11px] mt-0.5 opacity-90">{band.body}</p>
            {band.showTips && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {TIPS.map((tip) => (
                  <span
                    key={tip}
                    className="inline-flex items-center gap-1.5 text-sm font-medium bg-white/70 px-3 py-1.5 rounded-full border border-current/20"
                  >
                    <span className="h-1.5 w-1.5 rounded-full bg-current shrink-0" />
                    {tip}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {attempts > 1 && result && result.band !== "pass" && result.band !== "error" && (
        <p className="text-[10px] text-gray-400 mt-1.5 text-center" data-testid="mic-check-attempt-count">
          Attempt {attempts} of {MAX_ATTEMPTS_BEFORE_SKIP}
        </p>
      )}

      {/* Actions - retrying (warn/fail/silent/error) happens by tapping the
          pulsing mic button above, not a button here; this row only ever
          holds a genuine "move on" action. */}
      <div className="flex flex-wrap items-center justify-end gap-4 mt-3">
        {result?.band === "pass" && (
          <>
            <span
              className="text-[13px] font-medium text-app-primary underline decoration-transparent hover:decoration-current underline-offset-2 cursor-pointer transition"
              data-testid="mic-check-secondary-action"
              onClick={handleRetry}
            >
              Test again
            </span>
            <div className={primaryBtnClass} data-testid="mic-check-primary-action" onClick={() => onPassed()}>
              Continue
              <ArrowRight size={15} />
            </div>
          </>
        )}

        {result?.band === "warn" && (
          <div className={outlineBtnClass} data-testid="mic-check-secondary-action" onClick={() => onPassed()}>
            Continue anyway
          </div>
        )}

        {(result?.band === "fail" || result?.band === "silent") && canOfferSkip && (
          <div
            className={outlineBtnClass}
            data-testid="mic-check-secondary-action"
            onClick={() => onSkipped("max_attempts")}
          >
            Continue anyway
          </div>
        )}

        {result?.band === "error" && (
          <div
            className={primaryBtnClass}
            data-testid="mic-check-primary-action"
            onClick={() => onSkipped("deepgram_error")}
          >
            Start Interview
            <ArrowRight size={15} />
          </div>
        )}
      </div>
      </div>
    </div>
  );
};

export default MicCheck;
